import { type MaterialOrderItem, formatAmount } from '@/components/sklad/materialTypes';
import { formatDate } from '@/components/sklad/types';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useState } from 'react';

/**
 * Drobný materiál v kartě objednávky na Příjmu materiálu. Na rozdíl od rolí
 * se netiskne štítek — „Přijato" jen přičte množství na sklad.
 */
export function MaterialReceiveRows({
  items,
  pending,
  disabled,
  onDone,
  onError,
}: {
  items: MaterialOrderItem[];
  /** true = tab „Na cestě" (přijmout), false = „Přijato" (vrátit). */
  pending: boolean;
  disabled: boolean;
  onDone: (msg: string) => Promise<void>;
  onError: (msg: string) => void;
}) {
  const [busyId, setBusyId] = useState<number | null>(null);

  if (items.length === 0) return null;

  async function act(m: MaterialOrderItem) {
    setBusyId(m.id);
    try {
      await apiFetch(`/api/material/order-items/${m.id}/${pending ? 'receive' : 'unreceive'}`, {
        method: 'POST',
      });
      await onDone(
        pending
          ? `Přijato: ${m.label} (${formatAmount(m.quantity, m.unit)})`
          : `${m.label} vráceno na cestě`,
      );
    } catch (e: unknown) {
      onError(e instanceof ApiError ? e.describe() : 'Akce selhala');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="border-t border-border-light">
      <div className="px-5 pt-3 pb-1 text-xs font-semibold text-text-muted uppercase tracking-wider">
        Drobný materiál
      </div>
      <table className="w-full text-sm">
        <tbody>
          {items.map((m) => (
            <tr
              key={m.id}
              className="border-b border-border-light last:border-0 hover:bg-surface-hover transition-colors"
            >
              <td className="px-5 py-3 text-text-muted w-28">{m.category || '—'}</td>
              <td className="px-3 py-3 text-text-secondary">{m.label}</td>
              <td className="px-3 py-3 text-right text-text font-medium whitespace-nowrap">
                {formatAmount(m.quantity, m.unit)}
              </td>
              <td className="px-5 py-3 text-right whitespace-nowrap">
                {pending ? (
                  <button
                    type="button"
                    onClick={() => act(m)}
                    disabled={disabled || busyId === m.id}
                    className="inline-flex items-center gap-1.5 bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-4 py-2 cursor-pointer transition-colors disabled:opacity-50"
                  >
                    {busyId === m.id ? 'Přijímám…' : '✓ Přijato'}
                  </button>
                ) : (
                  <div className="flex items-center justify-end gap-2">
                    <span className="text-text-muted">Přijato {formatDate(m.received_at)}</span>
                    <button
                      type="button"
                      onClick={() => act(m)}
                      disabled={busyId === m.id}
                      title="Vrátit zpět mezi nepřijaté (oprava překliku) — odečte se ze skladu"
                      className="text-sm font-semibold text-text-secondary border border-border rounded-lg px-3 py-2 hover:bg-warning-light hover:text-warning transition-colors cursor-pointer disabled:opacity-50"
                    >
                      {busyId === m.id ? 'Vracím…' : 'Vrátit na cestě'}
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
